import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Era {
  id: string;
  name: string;
  description?: string;
  start_year?: number;
  end_year?: number;
}

export interface ForumPost {
  id: string;
  era_id: string;
  author_id: string | null;
  title: string;
  body: string;
  upvote_count: number;
  reply_count: number;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
  author_name: string | null;
  author_avatar: string | null;
  author_tier: string | null;
  media_urls?: string[];
  replies?: ForumReply[];
}

export interface ForumReply {
  id: string;
  post_id: string;
  author_id: string | null;
  parent_reply_id: string | null;
  body: string;
  upvote_count: number;
  created_at: string;
  author_name: string | null;
  author_avatar: string | null;
  author_tier: string | null;
  replies?: ForumReply[];
}

export interface HistorianAma {
  id: string;
  historian_id: string | null;
  title: string;
  description: string;
  era_id: string | null;
  scheduled_at: string;
  ended_at: string | null;
  status: 'upcoming' | 'live' | 'ended';
  question_count: number;
  created_at: string;
  historian_name: string | null;
  historian_avatar: string | null;
  historian_tier: string | null;
  era_name?: string | null;
}

export interface AmaQuestion {
  id: string;
  ama_id: string;
  asker_id: string | null;
  question: string;
  answer: string | null;
  answered_at: string | null;
  upvote_count: number;
  is_featured: boolean;
  created_at: string;
  asker_name: string | null;
  asker_avatar: string | null;
}

export interface GapChallenge {
  id: string;
  curator_id: string | null;
  era_id: string | null;
  title: string;
  description: string;
  reward_points: number;
  status: 'open' | 'in_progress' | 'resolved';
  response_count: number;
  deadline: string | null;
  created_at: string;
  curator_name: string | null;
  curator_avatar: string | null;
  era_name?: string | null;
}

export interface GapChallengeResponse {
  id: string;
  challenge_id: string;
  contributor_id: string | null;
  body: string;
  sources: string | null;
  upvote_count: number;
  is_accepted: boolean;
  created_at: string;
  contributor_name: string | null;
  contributor_avatar: string | null;
  contributor_tier: string | null;
}

// ─── Eras ─────────────────────────────────────────────────────────────────────

export async function getEras(): Promise<Era[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('eras')
    .select('id, label, sort_order')
    .order('sort_order', { ascending: true });
  if (error || !data) return [];
  // Map 'label' → 'name' to match the Era interface
  return data.map((row: any) => ({
    id: row.id,
    name: row.label,
    description: undefined,
    start_year: undefined,
    end_year: undefined,
  }));
}

// ─── Forum Posts ──────────────────────────────────────────────────────────────

export async function getForumPosts(eraId?: string): Promise<ForumPost[]> {
  const supabase = createClient();

  let query = supabase
    .from('era_forum_posts')
    .select('id, era_id, author_id, title, body, upvote_count, reply_count, is_pinned, created_at, updated_at, media_urls')
    .order('is_pinned', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(50);

  if (eraId) {
    query = query.eq('era_id', eraId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[getForumPosts] error:', error.message);
    return [];
  }
  if (!data) return [];

  const authorIds = [...new Set(data.filter((p: any) => p.author_id).map((p: any) => p.author_id))];
  const profileMap: Record<string, { full_name: string; avatar_url: string | null; tier: string }> = {};
  if (authorIds.length > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name, avatar_url, tier')
      .in('id', authorIds);
    for (const p of profiles || []) profileMap[p.id] = p;
  }

  return data.map((row: any) => {
    const profile = row.author_id ? profileMap[row.author_id] : null;
    return {
      ...row,
      author_name: profile?.full_name || null,
      author_avatar: profile?.avatar_url || null,
      author_tier: profile?.tier || null,
    };
  });
}

export async function createForumPost(params: {
  eraId: string;
  authorId: string;
  title: string;
  body: string;
  mediaUrls?: string[];
}): Promise<{ post: ForumPost | null; error: string | null }> {
  const supabase = createClient();

  const { data, error } = await supabase
    .from('era_forum_posts')
    .insert({
      era_id: params.eraId,
      author_id: params.authorId,
      title: params.title,
      body: params.body,
      media_urls: params.mediaUrls || [],
    })
    .select('id, era_id, author_id, title, body, upvote_count, reply_count, is_pinned, created_at, updated_at, media_urls')
    .single();

  if (error) {
    console.error('[createForumPost] Supabase error:', error.message, error.details, error.hint);
    return { post: null, error: error.message };
  }
  if (!data) {
    return { post: null, error: 'No data returned from insert' };
  }

  const { data: profile } = await supabase
    .from('user_profiles')
    .select('full_name, avatar_url, tier')
    .eq('id', params.authorId)
    .single();

  const post: ForumPost = {
    ...data,
    author_name: profile?.full_name || null,
    author_avatar: profile?.avatar_url || null,
    author_tier: profile?.tier || null,
  };

  return { post, error: null };
}

// ─── Forum Replies ────────────────────────────────────────────────────────────

export async function getForumReplies(postId: string): Promise<ForumReply[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('era_forum_replies')
    .select('id, post_id, author_id, parent_reply_id, body, upvote_count, created_at')
    .eq('post_id', postId)
    .order('created_at', { ascending: true });

  if (error || !data) return [];

  const authorIds = [...new Set(data.filter((r: any) => r.author_id).map((r: any) => r.author_id))];
  const profileMap: Record<string, { full_name: string; avatar_url: string | null; tier: string }> = {};
  if (authorIds.length > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name, avatar_url, tier')
      .in('id', authorIds);
    for (const p of profiles || []) profileMap[p.id] = p;
  }

  const replies: ForumReply[] = data.map((row: any) => {
    const profile = row.author_id ? profileMap[row.author_id] : null;
    return {
      ...row,
      author_name: profile?.full_name || null,
      author_avatar: profile?.avatar_url || null,
      author_tier: profile?.tier || null,
      replies: [],
    };
  });

  // Build threaded structure
  const byId: Record<string, ForumReply> = {};
  const topLevel: ForumReply[] = [];
  for (const r of replies) byId[r.id] = r;
  for (const r of replies) {
    if (r.parent_reply_id && byId[r.parent_reply_id]) {
      byId[r.parent_reply_id].replies = byId[r.parent_reply_id].replies || [];
      byId[r.parent_reply_id].replies!.push(r);
    } else {
      topLevel.push(r);
    }
  }
  return topLevel;
}

export async function createForumReply(params: {
  postId: string;
  authorId: string;
  body: string;
  parentReplyId?: string;
}): Promise<ForumReply | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('era_forum_replies')
    .insert({
      post_id: params.postId,
      author_id: params.authorId,
      body: params.body,
      parent_reply_id: params.parentReplyId || null,
    })
    .select('id, post_id, author_id, parent_reply_id, body, upvote_count, created_at')
    .single();

  if (error || !data) {
    console.error('[createForumReply] error:', error?.message);
    return null;
  }

  const { data: profile } = await supabase
    .from('user_profiles')
    .select('full_name, avatar_url, tier')
    .eq('id', params.authorId)
    .single();

  return {
    ...data,
    author_name: profile?.full_name || null,
    author_avatar: profile?.avatar_url || null,
    author_tier: profile?.tier || null,
    replies: [],
  };
}

// ─── Historian AMAs ───────────────────────────────────────────────────────────

export async function getHistorianAmas(): Promise<HistorianAma[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('historian_amas')
    .select('id, historian_id, title, description, era_id, scheduled_at, ended_at, status, question_count, created_at')
    .order('scheduled_at', { ascending: true });

  if (error || !data) return [];

  const historianIds = [...new Set(data.filter((a: any) => a.historian_id).map((a: any) => a.historian_id))];
  const profileMap: Record<string, { full_name: string; avatar_url: string | null; tier: string }> = {};
  if (historianIds.length > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name, avatar_url, tier')
      .in('id', historianIds);
    for (const p of profiles || []) profileMap[p.id] = p;
  }

  const eraIds = [...new Set(data.filter((a: any) => a.era_id).map((a: any) => a.era_id))];
  const eraMap: Record<string, string> = {};
  if (eraIds.length > 0) {
    // Use 'label' — the actual column name in the eras table
    const { data: eras } = await supabase.from('eras').select('id, label').in('id', eraIds);
    for (const e of eras || []) eraMap[e.id] = e.label;
  }

  return data.map((row: any) => {
    const profile = row.historian_id ? profileMap[row.historian_id] : null;
    return {
      ...row,
      historian_name: profile?.full_name || null,
      historian_avatar: profile?.avatar_url || null,
      historian_tier: profile?.tier || null,
      era_name: row.era_id ? eraMap[row.era_id] || null : null,
    };
  });
}

export async function getAmaQuestions(amaId: string): Promise<AmaQuestion[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('ama_questions')
    .select('id, ama_id, asker_id, question, answer, answered_at, upvote_count, is_featured, created_at')
    .eq('ama_id', amaId)
    .order('is_featured', { ascending: false })
    .order('upvote_count', { ascending: false });

  if (error || !data) return [];

  const askerIds = [...new Set(data.filter((q: any) => q.asker_id).map((q: any) => q.asker_id))];
  const profileMap: Record<string, { full_name: string; avatar_url: string | null }> = {};
  if (askerIds.length > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name, avatar_url')
      .in('id', askerIds);
    for (const p of profiles || []) profileMap[p.id] = p;
  }

  return data.map((row: any) => {
    const profile = row.asker_id ? profileMap[row.asker_id] : null;
    return {
      ...row,
      asker_name: profile?.full_name || null,
      asker_avatar: profile?.avatar_url || null,
    };
  });
}

export async function submitAmaQuestion(params: {
  amaId: string;
  askerId: string;
  question: string;
}): Promise<AmaQuestion | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('ama_questions')
    .insert({ ama_id: params.amaId, asker_id: params.askerId, question: params.question })
    .select('id, ama_id, asker_id, question, answer, answered_at, upvote_count, is_featured, created_at')
    .single();

  if (error || !data) return null;

  const { data: profile } = await supabase
    .from('user_profiles')
    .select('full_name, avatar_url')
    .eq('id', params.askerId)
    .single();

  return {
    ...data,
    asker_name: profile?.full_name || null,
    asker_avatar: profile?.avatar_url || null,
  };
}

// ─── Gap Challenges ───────────────────────────────────────────────────────────

export async function getGapChallenges(): Promise<GapChallenge[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('gap_challenges')
    .select('id, curator_id, era_id, title, description, reward_points, status, response_count, deadline, created_at')
    .order('created_at', { ascending: false });

  if (error || !data) return [];

  const curatorIds = [...new Set(data.filter((c: any) => c.curator_id).map((c: any) => c.curator_id))];
  const profileMap: Record<string, { full_name: string; avatar_url: string | null }> = {};
  if (curatorIds.length > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name, avatar_url')
      .in('id', curatorIds);
    for (const p of profiles || []) profileMap[p.id] = p;
  }

  const eraIds = [...new Set(data.filter((c: any) => c.era_id).map((c: any) => c.era_id))];
  const eraMap: Record<string, string> = {};
  if (eraIds.length > 0) {
    // Use 'label' — the actual column name in the eras table
    const { data: eras } = await supabase.from('eras').select('id, label').in('id', eraIds);
    for (const e of eras || []) eraMap[e.id] = e.label;
  }

  return data.map((row: any) => {
    const profile = row.curator_id ? profileMap[row.curator_id] : null;
    return {
      ...row,
      curator_name: profile?.full_name || null,
      curator_avatar: profile?.avatar_url || null,
      era_name: row.era_id ? eraMap[row.era_id] || null : null,
    };
  });
}

export async function getGapChallengeResponses(challengeId: string): Promise<GapChallengeResponse[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('gap_challenge_responses')
    .select('id, challenge_id, contributor_id, body, sources, upvote_count, is_accepted, created_at')
    .eq('challenge_id', challengeId)
    .order('is_accepted', { ascending: false })
    .order('upvote_count', { ascending: false });

  if (error || !data) return [];

  const contributorIds = [...new Set(data.filter((r: any) => r.contributor_id).map((r: any) => r.contributor_id))];
  const profileMap: Record<string, { full_name: string; avatar_url: string | null; tier: string }> = {};
  if (contributorIds.length > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name, avatar_url, tier')
      .in('id', contributorIds);
    for (const p of profiles || []) profileMap[p.id] = p;
  }

  return data.map((row: any) => {
    const profile = row.contributor_id ? profileMap[row.contributor_id] : null;
    return {
      ...row,
      contributor_name: profile?.full_name || null,
      contributor_avatar: profile?.avatar_url || null,
      contributor_tier: profile?.tier || null,
    };
  });
}

export async function submitGapChallengeResponse(params: {
  challengeId: string;
  contributorId: string;
  body: string;
  sources?: string;
}): Promise<GapChallengeResponse | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('gap_challenge_responses')
    .insert({
      challenge_id: params.challengeId,
      contributor_id: params.contributorId,
      body: params.body,
      sources: params.sources || null,
    })
    .select('id, challenge_id, contributor_id, body, sources, upvote_count, is_accepted, created_at')
    .single();

  if (error || !data) return null;

  const { data: profile } = await supabase
    .from('user_profiles')
    .select('full_name, avatar_url, tier')
    .eq('id', params.contributorId)
    .single();

  return {
    ...data,
    contributor_name: profile?.full_name || null,
    contributor_avatar: profile?.avatar_url || null,
    contributor_tier: profile?.tier || null,
  };
}

// ─── Community Activity Ticker ────────────────────────────────────────────────

export interface CommunityActivityItem {
  id: string;
  type: 'forum_post' | 'ama_question' | 'challenge_accepted' | 'contribution';
  title: string;
  snippet: string;
  author_name: string | null;
  author_avatar: string | null;
  author_tier: string | null;
  created_at: string;
  meta?: string;
}

export async function getLatestCommunityActivity(limit = 20): Promise<CommunityActivityItem[]> {
  const supabase = createClient();

  const [forumRes, amaRes, challengeRes, contribRes] = await Promise.all([
    supabase
      .from('era_forum_posts')
      .select('id, title, body, author_id, created_at, era_id')
      .order('created_at', { ascending: false })
      .limit(limit),
    supabase
      .from('ama_questions')
      .select('id, question, asker_id, created_at, ama_id')
      .order('created_at', { ascending: false })
      .limit(limit),
    supabase
      .from('gap_challenge_responses')
      .select('id, body, contributor_id, created_at, challenge_id')
      .order('created_at', { ascending: false })
      .limit(limit),
    supabase
      .from('contributions')
      .select('id, title, contributor_id, created_at')
      .order('created_at', { ascending: false })
      .limit(limit),
  ]);

  const userIds = new Set<string>();
  (forumRes.data || []).forEach((r: any) => r.author_id && userIds.add(r.author_id));
  (amaRes.data || []).forEach((r: any) => r.asker_id && userIds.add(r.asker_id));
  (challengeRes.data || []).forEach((r: any) => r.contributor_id && userIds.add(r.contributor_id));
  (contribRes.data || []).forEach((r: any) => r.contributor_id && userIds.add(r.contributor_id));

  const profileMap: Record<string, { full_name: string; avatar_url: string | null; tier: string }> = {};
  if (userIds.size > 0) {
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name, avatar_url, tier')
      .in('id', [...userIds]);
    for (const p of profiles || []) profileMap[p.id] = p;
  }

  // Collect era IDs for forum posts — use 'label' column
  const eraIds = new Set<string>();
  (forumRes.data || []).forEach((r: any) => r.era_id && eraIds.add(r.era_id));
  const eraMap: Record<string, string> = {};
  if (eraIds.size > 0) {
    const { data: eras } = await supabase.from('eras').select('id, label').in('id', [...eraIds]);
    for (const e of eras || []) eraMap[e.id] = e.label;
  }

  const amaIds = new Set<string>();
  (amaRes.data || []).forEach((r: any) => r.ama_id && amaIds.add(r.ama_id));
  const amaMap: Record<string, string> = {};
  if (amaIds.size > 0) {
    const { data: amas } = await supabase.from('historian_amas').select('id, title').in('id', [...amaIds]);
    for (const a of amas || []) amaMap[a.id] = a.title;
  }

  const challengeIds = new Set<string>();
  (challengeRes.data || []).forEach((r: any) => r.challenge_id && challengeIds.add(r.challenge_id));
  const challengeMap: Record<string, string> = {};
  if (challengeIds.size > 0) {
    const { data: challenges } = await supabase.from('gap_challenges').select('id, title').in('id', [...challengeIds]);
    for (const c of challenges || []) challengeMap[c.id] = c.title;
  }

  const items: CommunityActivityItem[] = [];

  for (const row of (forumRes.data || []) as any[]) {
    const p = row.author_id ? profileMap[row.author_id] : null;
    items.push({
      id: `forum_${row.id}`,
      type: 'forum_post',
      title: row.title,
      snippet: row.body?.slice(0, 80) || '',
      author_name: p?.full_name || null,
      author_avatar: p?.avatar_url || null,
      author_tier: p?.tier || null,
      created_at: row.created_at,
      meta: row.era_id ? eraMap[row.era_id] : undefined,
    });
  }

  for (const row of (amaRes.data || []) as any[]) {
    const p = row.asker_id ? profileMap[row.asker_id] : null;
    items.push({
      id: `ama_${row.id}`,
      type: 'ama_question',
      title: row.question?.slice(0, 80) || 'AMA Question',
      snippet: row.question || '',
      author_name: p?.full_name || null,
      author_avatar: p?.avatar_url || null,
      author_tier: p?.tier || null,
      created_at: row.created_at,
      meta: row.ama_id ? amaMap[row.ama_id] : undefined,
    });
  }

  for (const row of (challengeRes.data || []) as any[]) {
    const p = row.contributor_id ? profileMap[row.contributor_id] : null;
    items.push({
      id: `challenge_${row.id}`,
      type: 'challenge_accepted',
      title: row.challenge_id ? `Challenge: ${challengeMap[row.challenge_id] || 'Gap Challenge'}` : 'Gap Challenge',
      snippet: row.body?.slice(0, 80) || '',
      author_name: p?.full_name || null,
      author_avatar: p?.avatar_url || null,
      author_tier: p?.tier || null,
      created_at: row.created_at,
      meta: row.challenge_id ? challengeMap[row.challenge_id] : undefined,
    });
  }

  for (const row of (contribRes.data || []) as any[]) {
    const p = row.contributor_id ? profileMap[row.contributor_id] : null;
    items.push({
      id: `contrib_${row.id}`,
      type: 'contribution',
      title: row.title || 'New Contribution',
      snippet: row.title || '',
      author_name: p?.full_name || null,
      author_avatar: p?.avatar_url || null,
      author_tier: p?.tier || null,
      created_at: row.created_at,
    });
  }

  items.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  return items.slice(0, limit);
}
